/**
 * Configurações > Campos personalizados (#568, ADR-0023).
 *
 * ── O que a organização guarda além do de sempre ─────────────────────────
 *
 * Gestor e admin escolhem, cadastro por cadastro, os campos que a equipe
 * preenche: "Como conheceu" no cliente, "Forma de pagamento" na oportunidade.
 * Com a clínica ligada, também no pedido de exame e no tratamento. Quem atende
 * não vem aqui: só preenche, no próprio cadastro.
 *
 * ── Arquivar, e não apagar ───────────────────────────────────────────────
 *
 * Campo e opção arquivam. O que já foi preenchido continua no cadastro e na
 * exportação; o campo arquivado só sai do formulário. Ele vai para
 * "Arquivados", embaixo, e não entra na ordem, como as etapas de exame.
 *
 * ── O formulário no painel lateral (#923) ────────────────────────────────
 *
 * Criar e editar abrem no painel à direita, como todo formulário de criar. O
 * tipo só se escolhe ao criar: mudar um texto para lista deixaria o que já foi
 * escrito sem opção que o leia.
 */

import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CampoPersonalizado,
  EntidadeDeCampo,
  EtapaDoFunil,
  TipoDeCampo,
} from '@cliqcrm/contracts';
import { chaves } from '@/shared/api/chaves';
import { useApi, useCamposPersonalizados, useFunis } from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { useSessao } from '@/shared/sessao/contexto';
import {
  Botao,
  Campo,
  CampoSelecao,
  Chip,
  Dialogo,
  Icone,
  Interruptor,
  MenuAcoes,
  Segmentado,
  idDoPainel,
} from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import { campoFixoComONome } from '../lib/campos-fixos';
import { AcaoDaSecao } from './acao-da-secao';
import './campos-personalizados.css';

const NOMES_DOS_CADASTROS: Readonly<Record<EntidadeDeCampo, string>> = {
  cliente: 'Cliente',
  oportunidade: 'Oportunidade',
  pedido_de_exame: 'Pedido de exame',
  tratamento: 'Tratamento',
};

/** Onde a equipe encontra os campos de cada cadastro, para a nota ao lado. */
const ONDE_APARECE: Readonly<Record<EntidadeDeCampo, string>> = {
  cliente: 'Na aba Cadastro do Cliente 360 e no painel do cliente, ao lado da conversa.',
  oportunidade:
    'Na ficha da oportunidade e ao editar. O obrigatório aparece também ao criar e ao mudar de etapa.',
  pedido_de_exame: 'No cadastro do pedido de exame e no cartão dele, na aba Exames.',
  tratamento: 'No cadastro do tratamento e no cartão dele, na aba Tratamento.',
};

const NOMES_DOS_TIPOS: Readonly<Record<TipoDeCampo, string>> = {
  texto_curto: 'Texto curto',
  texto_longo: 'Texto longo',
  numero: 'Número',
  dinheiro: 'Valor em R$',
  data: 'Data',
  sim_nao: 'Sim ou não',
  lista_unica: 'Lista de opções (uma escolha)',
  lista_multipla: 'Lista de opções (várias escolhas)',
};

const TIPOS = Object.keys(NOMES_DOS_TIPOS) as TipoDeCampo[];
const COM_OPCOES: ReadonlySet<TipoDeCampo> = new Set(['lista_unica', 'lista_multipla']);
const DA_CLINICA: ReadonlySet<EntidadeDeCampo> = new Set(['pedido_de_exame', 'tratamento']);

/** A lista com o item trocado de lugar com o vizinho; na ponta, a mesma lista. */
function mover<T>(lista: readonly T[], indice: number, direcao: -1 | 1): T[] {
  const destino = indice + direcao;
  const nova = [...lista];
  if (destino < 0 || destino >= lista.length) return nova;
  [nova[indice], nova[destino]] = [nova[destino]!, nova[indice]!];
  return nova;
}

/** As etapas do funil, para o "obrigatório a partir de". Só a oportunidade pergunta. */
function useEtapasParaOObrigatorio(ligada: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: chaves.etapasDoFunil(),
    enabled: api !== null && ligada,
    queryFn: ({ signal }) => api!.etapasDoFunil({ sinal: signal }),
  });
}

export function SecaoCamposPersonalizados(): ReactNode {
  const { estado } = useSessao();
  const clinica = estado.nome === 'autenticado' && (estado.me.flags['clinica'] ?? false);
  const cadastros: EntidadeDeCampo[] = clinica
    ? ['cliente', 'oportunidade', 'pedido_de_exame', 'tratamento']
    : ['cliente', 'oportunidade'];

  const [cadastro, definirCadastro] = useState<EntidadeDeCampo>('cliente');
  /** `null` é o painel fechado; `'novo'` é criar; um campo é editar aquele. */
  const [painel, definirPainel] = useState<CampoPersonalizado | 'novo' | null>(null);

  return (
    <div className="campos-pers">
      <AcaoDaSecao>
        <Botao variante="primario" onClick={() => definirPainel('novo')}>
          Novo campo
        </Botao>
      </AcaoDaSecao>

      <Segmentado
        rotulo="Cadastro"
        aparencia="sublinhado"
        idDasAbas="campos-pers"
        opcoes={cadastros.map((valor) => ({ valor, rotulo: NOMES_DOS_CADASTROS[valor] }))}
        escolhida={cadastro}
        aoEscolher={definirCadastro}
      />

      <div
        className="campos-pers__corpo"
        role="tabpanel"
        id={idDoPainel('campos-pers', cadastro)}
        aria-labelledby={`campos-pers-${cadastro}`}
      >
        <CamposDoCadastro
          key={cadastro}
          cadastro={cadastro}
          aoEditar={(campo) => definirPainel(campo)}
          aoCriar={() => definirPainel('novo')}
        />
        <aside className="campos-pers__lado">
          <section className="campos-pers__cartao" aria-labelledby="campos-pers-onde">
            <h3 id="campos-pers-onde" className="campos-pers__titulo">
              Onde a equipe preenche
            </h3>
            <p className="campos-pers__nota">{ONDE_APARECE[cadastro]}</p>
            <p className="campos-pers__nota">
              Arquivar tira o campo do formulário e guarda o que já foi preenchido. Renomear não
              muda nada do que a equipe escreveu.
            </p>
          </section>
          <p className="admin__nota admin__nota--info">
            Só gestor e administrador mexem nesta lista. Quem atende vê os campos no cadastro e
            preenche lá.
          </p>
        </aside>
      </div>

      {painel !== null && (
        <PainelDoCampo
          cadastro={cadastro}
          campo={painel === 'novo' ? null : painel}
          clinica={clinica}
          aoFechar={() => definirPainel(null)}
        />
      )}
    </div>
  );
}

function CamposDoCadastro({
  cadastro,
  aoEditar,
  aoCriar,
}: {
  cadastro: EntidadeDeCampo;
  aoEditar: (campo: CampoPersonalizado) => void;
  aoCriar: () => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const campos = useCamposPersonalizados(cadastro);
  const etapas = useEtapasParaOObrigatorio(cadastro === 'oportunidade');
  const invalidar = () =>
    void cache.invalidateQueries({ queryKey: chaves.camposPersonalizados(cadastro) });

  const ordenar = useMutation({
    mutationFn: (ids: string[]) =>
      api!.ordenarCamposPersonalizados({ corpo: { entidade: cadastro, ids } }),
    onSuccess: invalidar,
  });

  const lista = campos.data?.itens ?? [];
  const vivos = lista.filter((campo) => !campo.arquivado);
  const arquivados = lista.filter((campo) => campo.arquivado);
  // Os arquivados vão no fim do pedido de ordem: só os vivos mudam de lugar.
  const reordenar = (indice: number, direcao: -1 | 1) =>
    ordenar.mutate([...mover(vivos, indice, direcao), ...arquivados].map((campo) => campo.id));
  const rotuloDaEtapa = (valor: string) =>
    etapas.data?.itens.find((etapa) => etapa.valor === valor)?.rotulo ?? valor;

  return (
    <div className="campos-pers__principal">
      {campos.isPending && <p className="campos-pers__nota">Buscando os campos…</p>}
      {campos.isError && <Faixa tom="bloqueado">Não foi possível buscar os campos.</Faixa>}
      {ordenar.isError && (
        <Faixa tom="bloqueado">
          {recadoDeErro(ordenar.error, { generica: 'Não foi possível mudar a ordem.' })}
        </Faixa>
      )}

      <section
        className="campos-pers__cartao"
        aria-label={`Campos de ${NOMES_DOS_CADASTROS[cadastro].toLocaleLowerCase('pt-BR')}`}
      >
        {campos.isSuccess && vivos.length === 0 && (
          <div className="campos-pers__vazio">
            <p className="campos-pers__nota">
              Nenhum campo próprio neste cadastro. A equipe preenche só os campos de sempre.
            </p>
            <Botao onClick={aoCriar}>Criar o primeiro campo</Botao>
          </div>
        )}
        {vivos.length > 0 && (
          <ol className="campos-pers__lista">
            {vivos.map((campo, indice) => (
              <LinhaDoCampo
                key={campo.id}
                campo={campo}
                posicao={indice + 1}
                primeira={indice === 0}
                ultima={indice === vivos.length - 1}
                ocupado={ordenar.isPending}
                rotuloDaEtapa={rotuloDaEtapa}
                aoMover={(direcao) => reordenar(indice, direcao)}
                aoEditar={() => aoEditar(campo)}
                aoMudar={invalidar}
              />
            ))}
          </ol>
        )}
      </section>

      {arquivados.length > 0 && (
        <section className="campos-pers__arquivados" aria-labelledby="campos-pers-arquivados">
          <h3 id="campos-pers-arquivados" className="campos-pers__versal">
            Arquivados · {arquivados.length}
          </h3>
          <ul className="campos-pers__lista">
            {arquivados.map((campo) => (
              <CampoArquivado key={campo.id} campo={campo} aoMudar={invalidar} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/** O resumo do campo em pílulas: o tipo, quantas opções e o obrigatório. */
function Resumo({
  campo,
  rotuloDaEtapa,
}: {
  campo: CampoPersonalizado;
  rotuloDaEtapa: (valor: string) => string;
}): ReactNode {
  const opcoesVivas = campo.opcoes.filter((opcao) => !opcao.arquivada).length;
  return (
    <span className="campos-pers__resumo">
      <span className="campos-pers__tipo">{NOMES_DOS_TIPOS[campo.tipo]}</span>
      {COM_OPCOES.has(campo.tipo) && (
        <Chip>{opcoesVivas === 1 ? '1 opção' : `${opcoesVivas} opções`}</Chip>
      )}
      {campo.obrigatorio && (
        <Chip tom="atencao">
          {campo.obrigatorio_desde_etapa
            ? `Obrigatório a partir de ${rotuloDaEtapa(campo.obrigatorio_desde_etapa)}`
            : 'Obrigatório'}
        </Chip>
      )}
    </span>
  );
}

function LinhaDoCampo({
  campo,
  posicao,
  primeira,
  ultima,
  ocupado,
  rotuloDaEtapa,
  aoMover,
  aoEditar,
  aoMudar,
}: {
  campo: CampoPersonalizado;
  posicao: number;
  primeira: boolean;
  ultima: boolean;
  ocupado: boolean;
  rotuloDaEtapa: (valor: string) => string;
  aoMover: (direcao: -1 | 1) => void;
  aoEditar: () => void;
  aoMudar: () => void;
}): ReactNode {
  const api = useApi();
  const arquivar = useMutation({
    mutationFn: () =>
      api!.editarCampoPersonalizado({
        caminho: { id: campo.id },
        corpo: { versao: campo.versao, arquivado: true },
      }),
    onSuccess: aoMudar,
  });

  return (
    <li className="campos-pers__linha">
      <span className="campos-pers__disco" aria-hidden="true">
        {posicao}
      </span>
      <span className="campos-pers__descricao">
        <span className="campos-pers__nome">{campo.nome}</span>
        <Resumo campo={campo} rotuloDaEtapa={rotuloDaEtapa} />
      </span>
      <span className="campos-pers__comandos">
        <Botao
          miudo
          icone
          variante="fantasma"
          rotulo={`Subir ${campo.nome}`}
          desabilitado={primeira || ocupado}
          motivo={primeira ? 'Já é o primeiro.' : 'Salvando…'}
          onClick={() => aoMover(-1)}
        >
          <span className="campos-pers__sobe">
            <Icone nome="abaixo" />
          </span>
        </Botao>
        <Botao
          miudo
          icone
          variante="fantasma"
          rotulo={`Descer ${campo.nome}`}
          desabilitado={ultima || ocupado}
          motivo={ultima ? 'Já é o último.' : 'Salvando…'}
          onClick={() => aoMover(1)}
        >
          <Icone nome="abaixo" />
        </Botao>
        <MenuAcoes
          rotulo={`Ações de ${campo.nome}`}
          itens={[
            { rotulo: 'Editar', aoClicar: aoEditar },
            {
              rotulo: 'Arquivar',
              separadorAntes: true,
              desabilitado: arquivar.isPending,
              motivo: 'Salvando…',
              aoClicar: () => arquivar.mutate(),
            },
          ]}
        />
      </span>
      {arquivar.isError && (
        <span className="campos-pers__erro">
          <Faixa tom="bloqueado">
            {recadoDeErro(arquivar.error, {
              generica: 'Não foi possível arquivar. Recarregue a página e tente de novo.',
            })}
          </Faixa>
        </span>
      )}
    </li>
  );
}

function CampoArquivado({
  campo,
  aoMudar,
}: {
  campo: CampoPersonalizado;
  aoMudar: () => void;
}): ReactNode {
  const api = useApi();
  const desarquivar = useMutation({
    mutationFn: () =>
      api!.editarCampoPersonalizado({
        caminho: { id: campo.id },
        corpo: { versao: campo.versao, arquivado: false },
      }),
    onSuccess: aoMudar,
  });
  return (
    <li className="campos-pers__arquivado">
      <span className="campos-pers__nome">{campo.nome}</span>
      <span className="campos-pers__apoio">
        fora do formulário; o que já foi preenchido continua guardado
      </span>
      <span className="campos-pers__comandos">
        <Botao
          miudo
          variante="secundario"
          aria-label={`Desarquivar ${campo.nome}`}
          desabilitado={desarquivar.isPending}
          motivo="Salvando…"
          onClick={() => desarquivar.mutate()}
        >
          Desarquivar
        </Botao>
      </span>
      {desarquivar.isError && (
        <span className="campos-pers__erro">
          <Faixa tom="bloqueado">
            {recadoDeErro(desarquivar.error, { generica: 'Não foi possível desarquivar o campo.' })}
          </Faixa>
        </span>
      )}
    </li>
  );
}

/** Uma opção no formulário: a que já existe leva o id; a nova, não. */
interface OpcaoNoFormulario {
  readonly chave: string;
  readonly id: string | null;
  readonly nome: string;
  readonly arquivada: boolean;
}

let proximaChave = 0;
const novaChave = () => `opcao-${++proximaChave}`;

function PainelDoCampo({
  cadastro,
  campo,
  clinica,
  aoFechar,
}: {
  cadastro: EntidadeDeCampo;
  /** `null` é criar. */
  campo: CampoPersonalizado | null;
  clinica: boolean;
  aoFechar: () => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const editando = campo !== null;
  const entidade = campo?.entidade ?? cadastro;

  const [nome, definirNome] = useState(campo?.nome ?? '');
  const [tipo, definirTipo] = useState<TipoDeCampo>(campo?.tipo ?? 'texto_curto');
  const [opcoes, definirOpcoes] = useState<OpcaoNoFormulario[]>(
    campo
      ? campo.opcoes.map((opcao) => ({ ...opcao, chave: opcao.id }))
      : [{ chave: novaChave(), id: null, nome: '', arquivada: false }],
  );
  const [obrigatorio, definirObrigatorio] = useState(campo?.obrigatorio ?? false);
  const [desdeEtapa, definirDesdeEtapa] = useState(campo?.obrigatorio_desde_etapa ?? '');

  const naOportunidade = entidade === 'oportunidade';
  const etapas = useEtapasParaOObrigatorio(naOportunidade);
  const funis = useFunis();
  const etapasPorFunil = useMemo(() => {
    const grupos = new Map<string, EtapaDoFunil[]>();
    for (const etapa of etapas.data?.itens ?? []) {
      grupos.set(etapa.funil, [...(grupos.get(etapa.funil) ?? []), etapa]);
    }
    return [...grupos.entries()].map(([chave, itens]) => ({
      nome: funis.data?.itens.find((funil) => funil.chave === chave)?.nome ?? chave,
      itens,
    }));
  }, [etapas.data, funis.data]);

  const comOpcoes = COM_OPCOES.has(tipo);
  const opcoesPreenchidas = opcoes.filter((opcao) => opcao.nome.trim() !== '');
  const semOpcaoViva = comOpcoes && !opcoesPreenchidas.some((opcao) => !opcao.arquivada);
  // A opção que já existia não sai nem fica sem nome: quem a escolheu continua com ela.
  const existenteSemNome =
    comOpcoes && opcoes.some((opcao) => opcao.id !== null && opcao.nome.trim() === '');
  const fixo = campoFixoComONome(entidade, nome, clinica);
  const podeSalvar = nome.trim() !== '' && !semOpcaoViva && !existenteSemNome;

  const salvar = useMutation({
    mutationFn: async () => {
      const etapa = obrigatorio && naOportunidade && desdeEtapa !== '' ? desdeEtapa : null;
      if (!campo) {
        return api!.criarCampoPersonalizado({
          corpo: {
            entidade,
            nome: nome.trim(),
            tipo,
            ...(comOpcoes
              ? { opcoes: opcoesPreenchidas.map((o) => ({ nome: o.nome.trim() })) }
              : {}),
            obrigatorio,
            obrigatorio_desde_etapa: etapa,
          },
        });
      }
      return api!.editarCampoPersonalizado({
        caminho: { id: campo.id },
        corpo: {
          versao: campo.versao,
          nome: nome.trim(),
          ...(comOpcoes
            ? {
                // A opção que já existia volta sempre: tirar uma é recusado.
                opcoes: opcoes
                  .filter((opcao) => opcao.id !== null || opcao.nome.trim() !== '')
                  .map((opcao) => ({
                    id: opcao.id,
                    nome: opcao.nome.trim(),
                    arquivada: opcao.arquivada,
                  })),
              }
            : {}),
          obrigatorio,
          obrigatorio_desde_etapa: etapa,
        },
      });
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: chaves.camposPersonalizados(entidade) });
      aoFechar();
    },
  });

  const mudarOpcao = (chave: string, mudanca: Partial<OpcaoNoFormulario>) =>
    definirOpcoes((atuais) =>
      atuais.map((opcao) => (opcao.chave === chave ? { ...opcao, ...mudanca } : opcao)),
    );

  return (
    <Dialogo
      titulo={editando ? `Editar ${campo.nome}` : 'Novo campo'}
      forma="lateral"
      aoFechar={() => {
        if (!salvar.isPending) aoFechar();
      }}
      botoes={
        <>
          <Botao
            variante="fantasma"
            onClick={aoFechar}
            desabilitado={salvar.isPending}
            motivo="Salvando…"
          >
            Cancelar
          </Botao>
          <Botao
            type="submit"
            form="campo-personalizado"
            variante="primario"
            desabilitado={!podeSalvar || salvar.isPending || api === null}
            motivo={
              salvar.isPending
                ? 'Salvando…'
                : nome.trim() === ''
                  ? 'Escreva o nome do campo.'
                  : existenteSemNome
                    ? 'Dê um nome a cada opção que já existia, ou arquive-a.'
                    : 'A lista precisa de pelo menos uma opção.'
            }
          >
            {salvar.isPending ? 'Salvando…' : editando ? 'Salvar' : 'Criar campo'}
          </Botao>
        </>
      }
    >
      <form
        id="campo-personalizado"
        className="campos-pers__formulario"
        onSubmit={(evento: FormEvent) => {
          evento.preventDefault();
          if (podeSalvar) salvar.mutate();
        }}
      >
        <p className="campos-pers__nota">
          Em <b>{NOMES_DOS_CADASTROS[entidade]}</b>. {ONDE_APARECE[entidade]}
        </p>
        <Faixa tom="atencao">
          {DA_CLINICA.has(entidade)
            ? 'O que a equipe preencher aqui é dado de saúde do paciente, e segue as regras da clínica. '
            : 'O que a equipe preencher aqui é dado do cliente, e é tratado como dado pessoal. '}
          Não use nome de pessoa no nome do campo nem nas opções.
        </Faixa>

        <Campo
          id="campo-personalizado-nome"
          rotulo="Nome do campo"
          type="text"
          value={nome}
          maxLength={60}
          autoFocus
          placeholder={
            entidade === 'oportunidade' ? 'Ex.: Forma de pagamento' : 'Ex.: Como conheceu'
          }
          onChange={(evento) => definirNome(evento.currentTarget.value)}
        />
        {fixo && (
          <Faixa tom="atencao">
            Este cadastro já tem o campo &ldquo;{fixo}&rdquo;. Um campo novo com o mesmo nome deixa
            a equipe sem saber qual preencher.
          </Faixa>
        )}

        <CampoSelecao
          id="campo-personalizado-tipo"
          rotulo="Tipo"
          value={tipo}
          disabled={editando}
          ajuda={
            editando
              ? 'O tipo não muda depois de criado. Para outro tipo, arquive este campo e crie outro.'
              : 'O tipo não muda depois de criado.'
          }
          onChange={(evento) => definirTipo(evento.currentTarget.value as TipoDeCampo)}
        >
          {TIPOS.map((valor) => (
            <option key={valor} value={valor}>
              {NOMES_DOS_TIPOS[valor]}
            </option>
          ))}
        </CampoSelecao>

        {comOpcoes && (
          <fieldset className="campos-pers__opcoes">
            <legend className="campos-pers__legenda">Opções</legend>
            <ul className="campos-pers__lista-opcoes">
              {opcoes.map((opcao, indice) => (
                <li
                  key={opcao.chave}
                  className="campos-pers__opcao"
                  data-arquivada={opcao.arquivada || undefined}
                >
                  <Campo
                    id={`campo-personalizado-${opcao.chave}`}
                    rotulo={`Opção ${indice + 1}`}
                    type="text"
                    value={opcao.nome}
                    maxLength={80}
                    disabled={opcao.arquivada}
                    onChange={(evento) =>
                      mudarOpcao(opcao.chave, { nome: evento.currentTarget.value })
                    }
                  />
                  {opcao.id === null ? (
                    <Botao
                      miudo
                      variante="fantasma"
                      aria-label={`Tirar a opção ${indice + 1}`}
                      onClick={() =>
                        definirOpcoes((atuais) => atuais.filter((o) => o.chave !== opcao.chave))
                      }
                    >
                      Tirar
                    </Botao>
                  ) : (
                    <Botao
                      miudo
                      variante="fantasma"
                      aria-label={`${opcao.arquivada ? 'Desarquivar' : 'Arquivar'} ${opcao.nome || `a opção ${indice + 1}`}`}
                      onClick={() => mudarOpcao(opcao.chave, { arquivada: !opcao.arquivada })}
                    >
                      {opcao.arquivada ? 'Desarquivar' : 'Arquivar'}
                    </Botao>
                  )}
                </li>
              ))}
            </ul>
            <Botao
              miudo
              variante="secundario"
              onClick={() =>
                definirOpcoes((atuais) => [
                  ...atuais,
                  { chave: novaChave(), id: null, nome: '', arquivada: false },
                ])
              }
            >
              Acrescentar opção
            </Botao>
            <p className="campos-pers__nota">
              A opção arquivada sai das escolhas novas; quem já a escolheu continua com ela.
            </p>
          </fieldset>
        )}

        <Interruptor
          ligado={obrigatorio}
          aoAlternar={() => definirObrigatorio((atual) => !atual)}
          rotulo="Obrigatório"
          descricao={
            naOportunidade
              ? 'A equipe não salva a oportunidade sem preencher. Pode valer só a partir de uma etapa.'
              : 'A equipe não salva o cadastro sem preencher.'
          }
        />
        {obrigatorio && naOportunidade && (
          <CampoSelecao
            id="campo-personalizado-etapa"
            rotulo="Obrigatório a partir de"
            value={desdeEtapa}
            onChange={(evento) => definirDesdeEtapa(evento.currentTarget.value)}
          >
            <option value="">Desde a criação da oportunidade</option>
            {etapasPorFunil.map((grupo) => (
              <optgroup key={grupo.nome} label={grupo.nome}>
                {grupo.itens.map((etapa) => (
                  <option key={etapa.valor} value={etapa.valor}>
                    {etapa.rotulo}
                  </option>
                ))}
              </optgroup>
            ))}
          </CampoSelecao>
        )}

        {salvar.isError && (
          <Faixa tom="bloqueado">
            {recadoDeErro(salvar.error, {
              generica: editando
                ? 'Não foi possível salvar o campo. Se outra pessoa o mudou agora, feche e abra de novo.'
                : 'Não foi possível criar o campo.',
            })}
          </Faixa>
        )}
      </form>
    </Dialogo>
  );
}
